import React, { Suspense, lazy, useEffect, useState } from 'react'
import { useAppDispatch } from '../../../store'
import { type Row, type ApplyFiltersAndDispatchParams } from '../../../components/CustomTable/types'
import PAGE_TITLES from '../../../consts/titles'
import LoadingPageV2 from '../../../pages/LoadingPageV2'
import { useParams } from 'react-router-dom'
import { getEnrollmentList, enrollmentsLoading } from '../../../store/slices/termsSectionsSlice/selectors'
import { DeleteEnrollment } from './DeleteEnrollment'
import { Box, Typography } from '@mui/material'
import { TopbarActions } from './TopbarActions'
import { getEnrollmentsApi } from '../../../store/slices/termsSectionsSlice/api'
import { exportCsv } from '../../../utils/exportCsv'
import { notification } from '../../../components/Notifications'
import { getTerm } from '../../../store/slices/termsSlice/selectors'
import { TermsManager } from '../../../APIManager'
import { useMutation } from '@tanstack/react-query'
import CreateEnrollment from './CreateEnrollment'

interface Instructor {
  userId: string
  name: string
  email: string
  isInstructorSelected?: boolean
}

const AppTable = lazy(async () => await import('../../../components/CustomTable'))

const Enrollments: React.FC = () => {
  const [params, setParams] = useState<ApplyFiltersAndDispatchParams>({ pageSize: 5 })

  const { id } = useParams()

  const loading = enrollmentsLoading()
  const enrollmentsList = getEnrollmentList()
  const dispatch = useAppDispatch()
  const term = getTerm()
  useEffect(() => {
    void dispatch(getEnrollmentsApi({ urlId: id, params }))
  }, [params, id])

  const { mutate: exportEnrollmentsCSV, isPending: exportCSVLoading } = useMutation({
    mutationFn: async ({ termId, params }: { termId: string, params: ApplyFiltersAndDispatchParams }) => await TermsManager.exportEnrollmentsCSV(termId, params),
    onSuccess: (response) => {
      const data = response?.data
      if (data) {
        exportCsv({ data, headers: response?.headers })
      }
    },
    onError: (error: any) => {
      notification(error?.message ?? 'Export csv failed', { variant: 'error' })
    }
  })

  const handleExport = (): void => {
    exportEnrollmentsCSV({ termId: term?.termId ?? '', params })
  }

  const overrides = {
    instructors: (row: Row) => {
      const { instructors = [], isEnrollmentForAllInstructors } = row

      if (!instructors.length) {
        return <Typography variant="body2" />
      }

      if (isEnrollmentForAllInstructors) {
        return <Typography variant="body2">--</Typography>
      }

      const names = instructors
        .filter((inst: Instructor) => inst?.isInstructorSelected)
        .map((inst: Instructor) => inst?.name?.trim())
        .filter(Boolean)
        .join(', ')

      return <Typography variant="body2">{names}</Typography>
    }
  }
  return (
    <Box mt={5}>
      <Suspense fallback={<LoadingPageV2 title={PAGE_TITLES.TERMS} />}>
        <AppTable
          createAction={<CreateEnrollment params={params} />}
          exportCsv={handleExport}
          exportButtonProps={{ disabled: exportCSVLoading, loading: exportCSVLoading }}
          customTopbarAction={<TopbarActions params={params} termId={term?.termId} />}
          editAction={<CreateEnrollment params={params} isEditAction={true} />}
          list={enrollmentsList}
          loading={loading}
          deleteAction={<DeleteEnrollment params={params} />}
          featureName="Student Enrollment"
          params={params}
          setParams={setParams}
          overrides={overrides}
        />
      </Suspense>
    </Box>
  )
}

export default Enrollments
